import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Plus, X, Trash2, Pencil, Sparkles, Quote, Copy, Target, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import FileAttachments from "@/components/FileAttachments";
import { confirmDialog } from "@/components/ConfirmDialog";

const STAGES = ["Sourcing", "Screening", "DD", "IC", "Closed Won", "Closed Lost"];

const empty = {
  name: "", sector: "", country: "", ticket_size_usd: 0, stage: "Sourcing",
  sponsor: "", contact_ids: [], notes: "", probability: 20,
};

function formatUSD(n) {
  if (!n) return "$0";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function PageHeader({ overline, title, action }) {
  return (
    <div className="border-b border-navy/10 px-8 py-8 bg-cream flex items-end justify-between">
      <div>
        <div className="overline text-gold-deep">{overline}</div>
        <h1 className="font-serif text-3xl md:text-4xl text-navy mt-2">{title}</h1>
        <div className="gold-rule mt-4" />
      </div>
      {action}
    </div>
  );
}

export default function Deals() {
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [memo, setMemo] = useState(null);
  const [memoLoading, setMemoLoading] = useState(false);
  const [endorseRequest, setEndorseRequest] = useState(null); // {deal, contact_id, custom_message, send_email, expires_days, result}
  const [endorseBusy, setEndorseBusy] = useState(false);
  const [pipelineMap, setPipelineMap] = useState({}); // {deal_id: {targeted, past_first_touch_pct, responded_pct, meetings_booked}}

  const load = async () => {
    const [d, c, p] = await Promise.all([
      api.get("/deals"),
      api.get("/contacts"),
      api.get("/pipeline/insights?include_closed=true").catch(() => ({ data: { deals: [] } })),
    ]);
    setDeals(d.data);
    setContacts(c.data);
    const map = {};
    for (const di of (p.data.deals || [])) map[di.deal_id] = di;
    setPipelineMap(map);
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [d, c, p] = await Promise.all([
        api.get("/deals"),
        api.get("/contacts"),
        api.get("/pipeline/insights?include_closed=true").catch(() => ({ data: { deals: [] } })),
      ]);
      if (cancelled) return;
      setDeals(d.data);
      setContacts(c.data);
      const map = {};
      for (const di of (p.data.deals || [])) map[di.deal_id] = di;
      setPipelineMap(map);
    })();
    return () => { cancelled = true; };
  }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (d) => { setEditing(d.id); setForm({ ...empty, ...d }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, ticket_size_usd: Number(form.ticket_size_usd) || 0, probability: Number(form.probability) || 0 };
      if (editing) await api.put(`/deals/${editing}`, payload);
      else await api.post("/deals", payload);
      toast.success(editing ? "Deal updated" : "Deal created");
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!(await confirmDialog({ title: "Delete this deal?", description: "The deal will be permanently removed from your pipeline.", confirmLabel: "Delete", destructive: true }))) return;
    await api.delete(`/deals/${id}`);
    toast.success("Deal deleted");
    load();
  };

  const moveStage = async (id, stage) => {
    const prev = deals.find((d) => d.id === id)?.stage;
    await api.patch(`/deals/${id}/stage`, { stage });
    await load();
    // When a deal lands in Closed Won (and wasn't already), prompt to request endorsement
    if (stage === "Closed Won" && prev !== "Closed Won") {
      const deal = deals.find((d) => d.id === id);
      if (deal && (deal.contact_ids?.length || 0) > 0) {
        toast.success("Deal closed won — request an endorsement?", {
          duration: 8000,
          action: {
            label: "Request",
            onClick: () => openEndorseRequest({ ...deal, stage }),
          },
        });
      } else {
        toast.success("Deal moved to Closed Won");
      }
    }
  };

  const openEndorseRequest = (deal) => {
    const linkedContacts = (deal.contact_ids || []).map((cid) => contacts.find((c) => c.id === cid)).filter(Boolean);
    setEndorseRequest({
      deal,
      linkedContacts,
      contact_id: linkedContacts[0]?.id || "",
      custom_message: "",
      send_email: true,
      expires_days: 30,
      result: null,
    });
  };

  const submitEndorseRequest = async () => {
    if (!endorseRequest?.contact_id) {
      toast.error("Pick a contact first.");
      return;
    }
    setEndorseBusy(true);
    try {
      const { data } = await api.post(`/deals/${endorseRequest.deal.id}/request-endorsement`, {
        contact_id: endorseRequest.contact_id,
        custom_message: endorseRequest.custom_message,
        send_email: endorseRequest.send_email,
        expires_days: Number(endorseRequest.expires_days) || 30,
      });
      setEndorseRequest((er) => ({ ...er, result: data }));
      if (data.email_sent) {
        toast.success(`Email sent to ${data.contact_email}`);
      } else if (data.email_error) {
        toast.message("Invite created — copy the link manually.", { description: data.email_error });
      } else {
        toast.success("Invite link generated.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Request failed");
    } finally {
      setEndorseBusy(false);
    }
  };

  const copyInviteLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  const generateMemo = async (deal) => {
    setMemoLoading(true);
    setMemo({ deal, content: "" });
    try {
      const { data } = await api.post("/ai/deal-memo", { deal_id: deal.id });
      setMemo({ deal, content: data.memo });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Memo generation failed");
      setMemo(null);
    } finally {
      setMemoLoading(false);
    }
  };

  const groupedByStage = STAGES.reduce((acc, s) => {
    acc[s] = deals.filter((d) => d.stage === s);
    return acc;
  }, {});

  return (
    <div data-testid="deals-page">
      <PageHeader
        overline="Pipeline"
        title="Deals"
        action={
          <button onClick={openCreate} data-testid="deals-add-btn" className="bg-navy text-gold px-4 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600 flex items-center gap-2">
            <Plus className="h-3.5 w-3.5" /> Add deal
          </button>
        }
      />
      <div className="p-8 overflow-x-auto">
        <div className="flex gap-4 min-w-max" data-testid="deals-kanban">
          {STAGES.map((stage) => (
            <div key={stage} className="w-72 flex-shrink-0" data-testid={`kanban-col-${stage.replace(/\s+/g, "-")}`}>
              <div className="flex items-center justify-between mb-3 border-b-2 border-gold/50 pb-2">
                <div className="overline text-navy">{stage}</div>
                <div className="text-xs text-navy/50">{groupedByStage[stage].length}</div>
              </div>
              <div className="space-y-3 min-h-[200px]">
                {groupedByStage[stage].map((d) => (
                  <div key={d.id} className="bg-white border border-navy/10 p-4 hover:border-gold transition-colors" data-testid={`deal-card-${d.id}`}>
                    <div className="font-serif text-base text-navy">{d.name}</div>
                    <div className="text-xs text-navy/60 mt-1">{d.sector} · {d.country}</div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="text-sm font-medium text-gold-deep">{formatUSD(d.ticket_size_usd)}</div>
                      <div className="text-xs text-navy/50">{d.probability}%</div>
                    </div>

                    {/* Pipeline health mini-widget */}
                    {pipelineMap[d.id] && pipelineMap[d.id].targeted > 0 && (
                      <Link
                        to={`/dashboard/pipeline`}
                        data-testid={`deal-pipeline-widget-${d.id}`}
                        className="mt-3 bg-cream/60 border-l-2 border-gold/60 p-2 block hover:border-gold transition-colors"
                      >
                        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-gold-deep mb-1">
                          <Target className="h-2.5 w-2.5" /> Pipeline
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-navy/85">
                            <strong>{pipelineMap[d.id].targeted}</strong> targeted
                          </span>
                          <span className="text-navy/65">
                            {pipelineMap[d.id].past_first_touch_pct}% touched
                          </span>
                          <span className={pipelineMap[d.id].responded_pct >= 20 ? "text-gold-deep font-medium" : "text-navy/65"}>
                            {pipelineMap[d.id].responded_pct}% replied
                          </span>
                        </div>
                        {pipelineMap[d.id].meetings_booked > 0 && (
                          <div className="text-[10px] uppercase tracking-widest text-gold-deep mt-1 flex items-center gap-1">
                            <TrendingUp className="h-2.5 w-2.5" />
                            {pipelineMap[d.id].meetings_booked} meeting{pipelineMap[d.id].meetings_booked > 1 ? "s" : ""} booked
                          </div>
                        )}
                      </Link>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                      <select
                        value={d.stage}
                        onChange={(e) => moveStage(d.id, e.target.value)}
                        data-testid={`deal-stage-select-${d.id}`}
                        className="text-xs border border-navy/15 bg-cream px-1.5 py-1 flex-1"
                      >
                        {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button onClick={() => generateMemo(d)} title="Generate AI memo" data-testid={`deal-memo-${d.id}`} className="text-gold-deep hover:text-gold">
                        <Sparkles className="h-4 w-4" />
                      </button>
                      {d.stage === "Closed Won" && (d.contact_ids?.length || 0) > 0 && (
                        <button onClick={() => openEndorseRequest(d)} title="Request endorsement" data-testid={`deal-endorse-${d.id}`} className="text-gold hover:text-gold-deep">
                          <Quote className="h-4 w-4" />
                        </button>
                      )}
                      <button onClick={() => openEdit(d)} data-testid={`deal-edit-${d.id}`} className="text-navy/60 hover:text-navy">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(d.id)} data-testid={`deal-delete-${d.id}`} className="text-navy/60 hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {groupedByStage[stage].length === 0 && (
                  <div className="text-xs text-navy/40 italic py-6 text-center border border-dashed border-navy/15">No deals</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-navy/40 flex items-center justify-center p-4" data-testid="deal-form-modal">
          <form onSubmit={save} className="bg-white max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="overline text-gold-deep">{editing ? "Edit" : "New"}</div>
                <h2 className="font-serif text-2xl text-navy">{editing ? "Edit deal" : "Add deal"}</h2>
              </div>
              <button type="button" onClick={() => setShowForm(false)} className="text-navy/50 hover:text-navy">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-5">
              <div className="col-span-2">
                <label className="overline text-navy/60">Deal name *</label>
                <input required data-testid="deal-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
              </div>
              <div>
                <label className="overline text-navy/60">Sector</label>
                <input data-testid="deal-input-sector" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })}
                  className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
              </div>
              <div>
                <label className="overline text-navy/60">Country</label>
                <input data-testid="deal-input-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                  className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
              </div>
              <div>
                <label className="overline text-navy/60">Ticket size (USD)</label>
                <input type="number" data-testid="deal-input-ticket" value={form.ticket_size_usd} onChange={(e) => setForm({ ...form, ticket_size_usd: e.target.value })}
                  className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
              </div>
              <div>
                <label className="overline text-navy/60">Probability (%)</label>
                <input type="number" min="0" max="100" data-testid="deal-input-probability" value={form.probability} onChange={(e) => setForm({ ...form, probability: e.target.value })}
                  className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
              </div>
              <div>
                <label className="overline text-navy/60">Stage</label>
                <select data-testid="deal-input-stage" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}
                  className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold">
                  {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="overline text-navy/60">Sponsor</label>
                <input data-testid="deal-input-sponsor" value={form.sponsor} onChange={(e) => setForm({ ...form, sponsor: e.target.value })}
                  className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
              </div>
              <div className="col-span-2">
                <label className="overline text-navy/60">Linked contacts</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {contacts.length === 0 && <div className="text-xs italic text-navy/40">No contacts yet — add some on the Contacts page.</div>}
                  {contacts.map((c) => {
                    const on = (form.contact_ids || []).includes(c.id);
                    return (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => setForm({
                          ...form,
                          contact_ids: on
                            ? form.contact_ids.filter((x) => x !== c.id)
                            : [...(form.contact_ids || []), c.id],
                        })}
                        data-testid={`deal-contact-toggle-${c.id}`}
                        className={`text-xs px-2.5 py-1 border ${on ? "bg-gold text-navy border-gold" : "text-navy/70 border-navy/20 hover:border-gold"}`}
                      >
                        {c.name}{c.organization ? ` · ${c.organization}` : ""}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="col-span-2">
                <label className="overline text-navy/60">Notes</label>
                <textarea rows={4} data-testid="deal-input-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full mt-1.5 border border-navy/15 bg-transparent p-3 focus:outline-none focus:border-gold resize-none text-sm" />
              </div>
              {editing && (
                <div className="col-span-2">
                  <FileAttachments dealId={editing} />
                </div>
              )}
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 text-xs uppercase tracking-widest text-navy/70 hover:text-navy">Cancel</button>
              <button type="submit" data-testid="deal-form-save" className="bg-navy text-gold px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600">
                {editing ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}

      {memo && (
        <div className="fixed inset-0 z-50 bg-navy/50 flex items-center justify-center p-4" data-testid="memo-modal">
          <div className="bg-white max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline text-gold-deep">AI Investment Memo</div>
                <h2 className="font-serif text-2xl text-navy">{memo.deal.name}</h2>
              </div>
              <button onClick={() => setMemo(null)} className="text-navy/50 hover:text-navy">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="gold-rule mb-6" />
            {memoLoading ? (
              <div className="py-12 text-center text-navy/60 italic">Drafting memo with Claude Sonnet 4.5…</div>
            ) : (
              <pre className="whitespace-pre-wrap font-sans text-sm text-navy/85 leading-relaxed">{memo.content}</pre>
            )}
          </div>
        </div>
      )}

      {endorseRequest && (
        <div className="fixed inset-0 z-50 bg-navy/40 flex items-center justify-center p-4" data-testid="endorse-request-modal">
          <div className="bg-white max-w-xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline text-gold-deep">{endorseRequest.deal.name} · Closed Won</div>
                <h2 className="font-serif text-2xl text-navy mt-1">Request an endorsement</h2>
              </div>
              <button onClick={() => setEndorseRequest(null)} className="text-navy/40 hover:text-navy">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="gold-rule mb-6" />

            {!endorseRequest.result ? (
              <>
                <div className="space-y-5">
                  {endorseRequest.linkedContacts.length === 0 ? (
                    <div className="text-sm text-destructive">
                      This deal has no linked contacts. Edit the deal and add a contact first.
                    </div>
                  ) : (
                    <div>
                      <label className="overline text-navy/60">Send to</label>
                      <select
                        data-testid="endorse-request-contact"
                        value={endorseRequest.contact_id}
                        onChange={(e) => setEndorseRequest({ ...endorseRequest, contact_id: e.target.value })}
                        className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
                      >
                        {endorseRequest.linkedContacts.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}{c.organization ? ` · ${c.organization}` : ""}{c.email ? ` (${c.email})` : " (no email)"}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="overline text-navy/60">Personal note (optional)</label>
                    <textarea
                      rows={3}
                      data-testid="endorse-request-message"
                      value={endorseRequest.custom_message}
                      onChange={(e) => setEndorseRequest({ ...endorseRequest, custom_message: e.target.value })}
                      placeholder="A line they'll see in the email — e.g. 'Thanks for trusting us on this one.'"
                      className="w-full mt-1.5 border border-navy/15 bg-transparent p-3 focus:outline-none focus:border-gold resize-none text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="overline text-navy/60">Link expires (days)</label>
                      <input
                        type="number"
                        min="0"
                        data-testid="endorse-request-expires"
                        value={endorseRequest.expires_days}
                        onChange={(e) => setEndorseRequest({ ...endorseRequest, expires_days: e.target.value })}
                        className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
                      />
                    </div>
                    <label className="flex items-end gap-2 pb-2 cursor-pointer text-sm text-navy/80">
                      <input
                        type="checkbox"
                        data-testid="endorse-request-send-email"
                        checked={endorseRequest.send_email}
                        onChange={(e) => setEndorseRequest({ ...endorseRequest, send_email: e.target.checked })}
                        className="accent-gold"
                      />
                      Send email immediately
                    </label>
                  </div>

                  <div className="text-xs text-navy/50 border-l-2 border-gold pl-3 italic">
                    The recipient gets a one-time link to a form with their name, role and organization pre-filled.
                    Endorsement appears on your public wall only after you approve it.
                  </div>
                </div>

                <div className="mt-8 flex justify-end gap-3">
                  <button type="button" onClick={() => setEndorseRequest(null)} className="px-5 py-2.5 text-xs uppercase tracking-widest text-navy/70">
                    Cancel
                  </button>
                  <button
                    onClick={submitEndorseRequest}
                    disabled={endorseBusy || endorseRequest.linkedContacts.length === 0}
                    data-testid="endorse-request-submit"
                    className="bg-navy text-gold px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600 disabled:opacity-50"
                  >
                    {endorseBusy ? "Creating…" : endorseRequest.send_email ? "Create link & send email" : "Create link"}
                  </button>
                </div>
              </>
            ) : (
              <div data-testid="endorse-request-result">
                <div className="overline text-gold-deep mb-2">
                  {endorseRequest.result.email_sent ? "Sent" : "Invite link ready"}
                </div>
                <div className="bg-cream border border-navy/10 p-3 flex items-center gap-3 mb-3">
                  <code className="flex-1 text-xs text-navy/80 truncate" data-testid="endorse-request-url">
                    {endorseRequest.result.invite_url}
                  </code>
                  <button
                    onClick={() => copyInviteLink(endorseRequest.result.invite_url)}
                    data-testid="endorse-request-copy"
                    className="text-xs uppercase tracking-widest text-gold-deep hover:text-navy flex items-center gap-1.5"
                  >
                    <Copy className="h-3.5 w-3.5" /> Copy
                  </button>
                </div>
                {endorseRequest.result.email_sent ? (
                  <p className="text-sm text-navy/80">
                    Email sent to <span className="font-medium">{endorseRequest.result.contact_email}</span>.
                    The interaction is logged on the deal & contact timeline.
                  </p>
                ) : (
                  <p className="text-sm text-navy/70">
                    {endorseRequest.result.email_error || "Copy the link and send it manually."}
                  </p>
                )}
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setEndorseRequest(null)}
                    data-testid="endorse-request-done"
                    className="bg-navy text-gold px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
